import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DtsenField } from '@/hooks/use-bnba-datasets';

interface RowFilterEditorProps {
  /** Filterable fields of one set that the upload carries. */
  fields: DtsenField[];
  value: Record<string, string[]>;
  onChange: (value: Record<string, string[]>) => void;
}

/** Splits "6571, 6501" into distinct values. */
function parseValues(text: string): string[] {
  return Array.from(
    new Set(
      text
        .split(/[,\s]+/)
        .map((v) => v.trim())
        .filter(Boolean)
    )
  );
}

/**
 * Row scope of a build: rows are kept when, for every field listed, the row
 * carries one of that field's values.
 */
export function RowFilterEditor({ fields, value, onChange }: RowFilterEditorProps) {
  const [adding, setAdding] = useState('');
  const byName = new Map(fields.map((f) => [f.name, f]));
  const unused = fields.filter((f) => !(f.name in value));

  const set = (name: string, values: string[]) => onChange({ ...value, [name]: values });
  const remove = (name: string) => {
    const { [name]: _dropped, ...rest } = value;
    onChange(rest);
  };

  return (
    <div className="space-y-3">
      {Object.entries(value).map(([name, values]) => {
        const field = byName.get(name);
        return (
          <div key={name} className="space-y-2 rounded-lg border p-3">
            <div className="flex items-center justify-between gap-2">
              <code className="text-xs font-medium">{name}</code>
              <Button type="button" variant="ghost" size="sm" onClick={() => remove(name)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            {field?.codes ? (
              <div className="grid gap-1 md:grid-cols-2">
                {Object.entries(field.codes).map(([code, label]) => (
                  <label key={code} className="flex items-start gap-2 text-sm">
                    <Checkbox
                      className="mt-0.5"
                      checked={values.includes(code)}
                      onCheckedChange={(v) =>
                        set(
                          name,
                          v === true ? [...values, code] : values.filter((x) => x !== code)
                        )
                      }
                    />
                    <span>
                      <span className="font-medium">{code}</span> — {label}
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <Input
                placeholder="Nilai dipisah koma, mis. 6571, 6501"
                defaultValue={values.join(', ')}
                onBlur={(e) => set(name, parseValues(e.target.value))}
              />
            )}
            {values.length === 0 ? (
              <p className="text-xs text-destructive">Isi minimal satu nilai.</p>
            ) : null}
          </div>
        );
      })}

      {unused.length > 0 ? (
        <div className="flex items-center gap-2">
          <Select value={adding} onValueChange={setAdding}>
            <SelectTrigger className="max-w-sm">
              <SelectValue placeholder="Batasi baris berdasarkan…" />
            </SelectTrigger>
            <SelectContent>
              {unused.map((f) => (
                <SelectItem key={f.name} value={f.name}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!adding}
            onClick={() => {
              set(adding, []);
              setAdding('');
            }}
          >
            <Plus className="mr-1 h-4 w-4" />
            Tambah
          </Button>
        </div>
      ) : null}
    </div>
  );
}
